import {MARKETPLACE_ORDER_STATUS_LABELS, SUPPLY_STATUS_LABELS} from '../../constants/dictionaries';
import {routeUrls} from '../../constants/routeUrls';
import type {ChatSubjectFieldsFragment, ChatSubjectType} from '../../gqlApp/graphql';

/**
 * Объекты обсуждений в кабинете (SKS-16).
 *
 * Одно место на подпись, статус и ссылку: их спрашивают кнопка в карточке объекта,
 * шапка ветки и чип в общей ленте, и три копии правила «как назвать возврат»
 * разошлись бы при первой же правке формулировки.
 */

/** Как называется объект ветки. Возврат — по заказу: своего номера у него нет. */
export const CHAT_SUBJECT_LABELS: Record<ChatSubjectType, string> = {
	SUPPLY: 'Поставка',
	ORDER: 'Заказ',
	RETURN: 'Возврат по заказу',
};

/** Что написать на кнопке в карточке объекта. */
export const CHAT_SUBJECT_ACTIONS: Record<ChatSubjectType, string> = {
	SUPPLY: 'Обсудить поставку',
	ORDER: 'Обсудить заказ',
	RETURN: 'Обсудить возврат',
};

export interface ChatSubjectParams {
	subjectType: ChatSubjectType;
	subjectId: string;
}

const SUBJECT_TYPES: ChatSubjectType[] = ['SUPPLY', 'ORDER', 'RETURN'];

/**
 * Форма идентификатора объекта. Проверяется здесь, потому что на сервере это колонка
 * `uuid`: строка не той формы роняет сам запрос ленты, и вместо чата человек видит
 * пустую страницу без объяснения. Не та форма — значит ветки нет, открывается общая лента.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Ветка из строки запроса чата.
 *
 * Значение проверяется перечнем: адрес правят руками и присылают друг другу,
 * и `?subjectType=supply` должен открыть общую ленту, а не сломать страницу
 * запросом, на который сервер ответит ошибкой схемы.
 */
export function parseChatSubject(params: URLSearchParams): ChatSubjectParams | undefined {
	const subjectType = params.get('subjectType');
	const subjectId = params.get('subjectId');

	if (
		!subjectId ||
		!UUID.test(subjectId) ||
		!SUBJECT_TYPES.includes(subjectType as ChatSubjectType)
	) {
		return undefined;
	}

	return {subjectType: subjectType as ChatSubjectType, subjectId};
}

/** «Поставка SUP-2026-000042» — подпись объекта одной строкой. */
export function subjectTitle(card: ChatSubjectFieldsFragment): string {
	return `${CHAT_SUBJECT_LABELS[card.subjectType]} ${card.number}`;
}

/**
 * Статус объекта словами. Пусто, когда статуса у карточки нет.
 *
 * У ветки возврата статуса нет намеренно: в карточке лежит статус заказа, и подпись
 * «Возврат по заказу ORD-… · Отменён» читалась бы так, будто отменён сам возврат.
 * Где товар на самом деле — показывает блок «Что с товаром» на карточке заказа.
 */
export function subjectStatusText(card: ChatSubjectFieldsFragment): string | undefined {
	if (card.supplyStatus) {
		return SUPPLY_STATUS_LABELS[card.supplyStatus];
	}

	if (card.subjectType === 'RETURN') {
		return undefined;
	}

	return card.orderStatus ? MARKETPLACE_ORDER_STATUS_LABELS[card.orderStatus] : undefined;
}

/**
 * Куда ведёт карточка объекта. У возврата своей страницы нет — он показан блоком
 * «Что с товаром» на карточке заказа (SKS-121), туда и ведём.
 */
export function subjectHref(companyId: string, card: ChatSubjectFieldsFragment): string {
	return card.subjectType === 'SUPPLY'
		? routeUrls.supplyCard(companyId, card.subjectId)
		: routeUrls.orderCard(companyId, card.subjectId);
}
